// components/ui/notionColors.ts
// Exact Notion dark-mode tag palette (text + background hex) — used to colour
// status / state pills the way Notion colours its select/status tags.
// Values are Notion's dark-theme tag colours (text colour + the subtly tinted
// dark background it pairs with).

export type NotionColorName =
  | 'default'
  | 'gray'
  | 'brown'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'pink'
  | 'red';

export const NOTION_DARK_COLORS: Record<NotionColorName, { text: string; bg: string }> = {
  default: { text: '#D4D4D4', bg: '#191919' },
  gray: { text: '#9B9B9B', bg: '#252525' },
  brown: { text: '#A27763', bg: '#2E2724' },
  orange: { text: '#CB7B37', bg: '#36291F' },
  yellow: { text: '#C19138', bg: '#372E20' },
  green: { text: '#4F9768', bg: '#242B26' },
  blue: { text: '#447ACB', bg: '#1F282D' },
  purple: { text: '#865DBB', bg: '#2A2430' },
  pink: { text: '#BA4A78', bg: '#2E2328' },
  red: { text: '#BE524B', bg: '#332523' },
};

/**
 * Maps a normalized entity/campaign status to a Notion colour name.
 * Covers the sourcing fit + campaign status vocabularies.
 */
const statusNotionColor = (status: string | null | undefined): NotionColorName => {
  switch ((status || '').toUpperCase()) {
    case 'SUCCESS':
    case 'COMPLETE':
    case 'COMPLETED':
    case 'DONE':
      return 'green';
    case 'RUNNING':
    case 'IN_PROGRESS':
      return 'blue';
    case 'PENDING':
    case 'WAITING':
    case 'QUEUED':
      return 'yellow';
    case 'INTERESTING':
      return 'purple';
    case 'REVERTED':
      return 'brown';
    case 'ABORTED':
      return 'orange';
    case 'FAILED':
    case 'ERROR':
      return 'red';
    case 'DISMISSED':
    case 'INVALIDATED':
      return 'gray';
    default:
      return 'gray';
  }
};

/** Inline style for a status pill, using the Notion dark tag colours. */
export const notionStatusPillStyle = (
  status: string | null | undefined,
): { backgroundColor: string; color: string } => {
  const c = NOTION_DARK_COLORS[statusNotionColor(status)];
  return { backgroundColor: c.bg, color: c.text };
};
